import { Grid, Avatar, IconButton, Tooltip, Box, Stack, Alert } from "@mui/material";
import MDInput from "components/Basics/MDInput";
import MDButton from "components/Basics/MDButton";
import MDTypography from "components/Basics/MDTypography";
import ClearIcon from "@mui/icons-material/Clear";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LinkIcon from "@mui/icons-material/Link";
import { useState, useEffect } from "react";
import { t } from "i18next";
import { uploadFileToS3, deleteFileFromPath, uploadFileFromUrlToS3 } from "helpers/s3";
import lod_ from "lodash";

/**
 * @typedef {object} UploadedImageValue
 * @property {string} [url] Public URL of the uploaded image.
 * @property {string} [key] Storage key used to delete the file.
 * @property {string} [path] Storage folder path.
 * @property {string} [fileName] Original or generated file name.
 */

/**
 * Image input supporting both file upload and URL import.
 * Also handles migration of legacy values stored as raw URL strings.
 *
 * @param {object} props Component props.
 * @param {string} props.label Display label shown above the field.
 * @param {UploadedImageValue|string|null} props.value Current value; can be an uploaded image object or legacy URL string.
 * @param {(value: UploadedImageValue|null) => void} props.onChange Callback fired when the image value changes.
 * @param {Object} [props.item] Form item metadata.
 * @param {string} [props.path] Optional form path.
 * @param {Array} [props.listOfApis=[]] Optional API list used by parent forms.
 * @param {boolean} [props.disabled] Whether inputs are disabled.
 * @param {string|boolean} [props.error] External error state from parent form.
 * @param {object} [props.InputProps={}] Optional input props forwarded by parent.
 * @returns {JSX.Element}
 */
const InputUploadImage = ({
	label,
	value,
	onChange,
	item = {},
	path,
	listOfApis = [],
	disabled,
	error,
	InputProps = {},
	...rest
}) => {
	const [fileURL, setFileURL] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [isMigratingLegacy, setIsMigratingLegacy] = useState(false);

	useEffect(() => {
		if (errorMessage) {
			const timer = setTimeout(() => {
				setErrorMessage("");
			}, 5000);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [errorMessage]);

	/**
	 * Uploads a selected image file to S3 and propagates the resulting file object.
	 *
	 * @param {File} file Browser file selected by the user.
	 * @returns {Promise<void>}
	 */
	const uploadImage = async file => {
		if (!file?.type.startsWith("image/")) {
			setErrorMessage(t("FORMS.errorNotImage"));
			return;
		}
		const tempAnswerItemID = `temp_${Date.now()}`;
		const uploadResult = await uploadFileToS3(file, tempAnswerItemID, "image", "fr", true);

		if (uploadResult?.fileObject) {
			onChange(uploadResult.fileObject);
			setFileURL("");
		} else {
			setErrorMessage(t("FORMS.errorInUpload"));
		}
	};

	/**
	 * Handles native file input changes and resets the input value
	 * so the same file can be selected again after an error.
	 *
	 * @param {Event} e Native input change event.
	 * @returns {Promise<void>}
	 */
	const handleImageChange = async e => {
		const { files } = e.target;
		const file = files?.[0];
		if (file) await uploadImage(file);
		// Allow selecting the same file again (e.g., retry after failed upload).
		e.target.value = "";
	};

	/**
	 * Extracts a file name from a URL path.
	 *
	 * @param {string} imageUrl URL to parse.
	 * @returns {string|null} File name when available, otherwise null.
	 */
	const getFileNameFromUrl = imageUrl => {
		try {
			const { pathname } = new URL(imageUrl);
			const name = pathname.split("/").pop();
			return name || null;
		} catch (error) {
			return null;
		}
	};

	/**
	 * Upload an image to S3 from a remote URL via backend download/upload.
	 * @param {string} imageUrl - The URL of the image to fetch and upload
	 * @returns {Promise<void>}
	 */
	const uploadImageFromUrl = async imageUrl => {
		try {
			setErrorMessage("");
			const tempAnswerItemID = `temp_${Date.now()}`;
			const fileName = getFileNameFromUrl(imageUrl);

			const uploadResult = await uploadFileFromUrlToS3(
				imageUrl,
				tempAnswerItemID,
				"image",
				"fr",
				fileName,
				true
			);

			if (uploadResult?.success && !lod_.isEmpty(uploadResult?.result?.fileObject)) {
				onChange(uploadResult.result.fileObject);
				setFileURL("");
				return;
			}

			setErrorMessage(t("FORMS.errorInUpload"));
		} catch (err) {
			setErrorMessage(t("FORMS.errorInUpload"));
		}
	};

	const handleURLImageChange = async () => {
		await uploadImageFromUrl(fileURL);
	};

	/**
	 * Copies a text value to clipboard when browser APIs are available.
	 *
	 * @param {string} text Value to copy.
	 * @returns {Promise<void>}
	 */
	const copyTextToClipboard = async text => {
		if (lod_.isEmpty(text)) return;
		try {
			await window.navigator.clipboard.writeText(text);
		} catch (err) {
			// Silently ignore clipboard errors to keep the upload UX uninterrupted.
		}
	};

	/**
	 * Detect and migrate a legacy image format (raw URL string) to modern S3 format
	 * Checks if the current value is a string URL, then uploads it as a new image
	 * @returns {Promise<void>}
	 * @example
	 * await handleLegacyMigration();
	 * // If value is a URL string, migration UI shows and uploads to S3 on click
	 */
	const handleLegacyMigration = async () => {
		if (!lod_.isString(value) || !value.startsWith("http")) return;

		setIsMigratingLegacy(true);
		try {
			await uploadImageFromUrl(value);
		} finally {
			setIsMigratingLegacy(false);
		}
	};

	/**
	 * Deletes the currently uploaded image from storage and clears the field value.
	 *
	 * @returns {Promise<void>}
	 */
	const deleteImage = async () => {
		if (!value?.key) return;
		await deleteFileFromPath({
			key: value.key,
			path: value.path,
			fileName: value.fileName,
			publicBucket: true
		});
		onChange(null);
	};

	if (lod_.isString(value) && value.startsWith("http")) {
		return (
			<Grid
				size="grow"
				sx={{
					flexBasis: "100%",
					minWidth: "100%",
					backgroundColor: "#ffffff",
					border: "1px solid #e0e0e0",
					borderRadius: 2,
					p: 2
				}}
			>
				<MDTypography variant="body2" sx={{ mb: 1, fontWeight: "bold" }}>
					{label}
				</MDTypography>

				<Alert severity="warning" sx={{ mb: 2 }}>
					{t("FORMS.legacyImageFormatNotice", {
						defaultValue:
							"Image en ancien format detectée. Cliquez sur migrer pour la convertir et sauvegarder le nouveau format."
					})}
				</Alert>

				{errorMessage && (
					<Alert severity="error" onClose={() => setErrorMessage("")} sx={{ mb: 2 }}>
						{errorMessage}
					</Alert>
				)}

				<Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
					<Avatar
						src={value}
						onClick={() => copyTextToClipboard(value)}
						title={t("FORMS.copyToClipboard")}
						sx={{
							width: 100,
							height: 100,
							objectFit: "cover",
							border: "2px solid #f0f0f0"
						}}
					/>

					<MDButton
						color="info"
						variant="outlined"
						onClick={handleLegacyMigration}
						disabled={disabled || isMigratingLegacy}
					>
						<CloudUploadIcon sx={{ mr: 1, fontSize: 18 }} />
						{isMigratingLegacy
							? t("FORMS.migratingImage", { defaultValue: "Migration en cours..." })
							: t("FORMS.migrateImage", { defaultValue: "Migrer l'image" })}
					</MDButton>
				</Stack>
			</Grid>
		);
	}

	return (
		<Grid
			size="grow"
			sx={{
				flexBasis: "100%",
				minWidth: "100%",
				backgroundColor: "#ffffff",
				border: "1px solid #e0e0e0",
				borderRadius: 2,
				p: 2
			}}
		>
			<MDTypography variant="body2" sx={{ mb: 1, fontWeight: "bold" }}>
				{label}
			</MDTypography>

			{errorMessage && (
				<Alert severity="error" onClose={() => setErrorMessage("")} sx={{ mb: 2 }}>
					{errorMessage}
				</Alert>
			)}

			{value?.url ? (
				<Box sx={{ position: "relative", width: "fit-content" }}>
					<Avatar
						src={value.url}
						onClick={() => copyTextToClipboard(value.url)}
						title={t("FORMS.copyToClipboard")}
						sx={{
							width: 100,
							height: 100,
							objectFit: "cover",
							border: "2px solid #f0f0f0"
						}}
					/>
					<Tooltip title={t("FORMS.LABELS.delete")}>
						<IconButton
							size="small"
							onClick={deleteImage}
							sx={{
								position: "absolute",
								top: -8,
								right: -8,
								backgroundColor: "error.main",
								color: "white",
								"&:hover": { backgroundColor: "error.dark" },
								width: 24,
								height: 24
							}}
						>
							<ClearIcon sx={{ fontSize: 16 }} />
						</IconButton>
					</Tooltip>
				</Box>
			) : (
				<Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
					{/* Upload File */}
					<Box
						component="label"
						htmlFor="image-input-upload"
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							width: 120,
							height: 120,
							p: 2,
							border: "2px dashed #ccc",
							borderRadius: 2,
							cursor: disabled ? "not-allowed" : "pointer",
							transition: "all 0.2s",
							"&:hover": {
								backgroundColor: "action.hover"
							},
							opacity: disabled ? 0.5 : 1
						}}
					>
						<input
							id="image-input-upload"
							type="file"
							accept="image/*"
							onChange={handleImageChange}
							disabled={disabled}
							style={{ display: "none" }}
							aria-label={t("FORMS.uploadImage")}
						/>
						<Tooltip title={t("FORMS.uploadImage")}>
							<Box
								sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}
							>
								<CloudUploadIcon sx={{ fontSize: 36 }} />
								<MDTypography variant="caption" sx={{ textAlign: "center", fontSize: "0.65rem" }}>
									{t("FORMS.uploadImage")}
								</MDTypography>
							</Box>
						</Tooltip>
					</Box>

					{/* OR Divider */}
					<MDTypography variant="body2" sx={{ textTransform: "uppercase", fontWeight: 500 }}>
						{t("FORMS.or")}
					</MDTypography>

					{/* URL Input */}
					<Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
						<MDInput
							size="small"
							placeholder={t("FORMS.labelUploadImageURL")}
							value={fileURL}
							onChange={e => setFileURL(e.target.value)}
							disabled={disabled}
							fullWidth
						/>
						<MDButton
							color="info"
							variant="contained"
							disabled={!fileURL?.trim() || disabled}
							onClick={handleURLImageChange}
							fullWidth
						>
							<LinkIcon sx={{ mr: 1, fontSize: 18 }} />
							{t("FORMS.uploadImageURL")}
						</MDButton>
					</Box>
				</Stack>
			)}
		</Grid>
	);
};

export default InputUploadImage;
